import { Container, Sprite, Texture } from 'pixi.js';
import { AssetLoader } from './AssetLoader';

export class RivalCatCameo extends Container {
  private sprite: Sprite;
  private runFrames: Texture[];
  private poopTexture: Texture;

  // Animation states
  private state: 'walk_in' | 'poop' | 'poop_pause' | 'walk_out' = 'walk_in';
  private timer = 0;
  private isDone = false;
  private poopSpawnX = 0;

  // Multiple poop logic
  private totalPoopsPlanned = 1;
  private poopCount = 0;
  private poopPauseDuration = 0.3; // Delay between poops

  // Walk animation
  private currentFrame = 0;
  private animationTimer = 0;
  private animationSpeed = 0.1;

  // Timing
  private walkInDuration = 0.6;
  private poopDuration = 0.4;
  private walkOutDuration = 0.6;

  // Movement
  private walkSpeed = 400;
  private targetX = 0;

  private onTelegraphCompleteCallback?: () => void;
  private onPoopSpawnCallback?: () => void;

  constructor(gameWidth: number, groundY: number, rivalCatType: 'alici' | 'jasper' = 'jasper') {
    super();

    // Determine number of poops: 60% single, 30% double, 10% triple
    const randomValue = Math.random();
    if (randomValue < 0.1) {
      this.totalPoopsPlanned = 3; // 10% chance for triple poop
    } else if (randomValue < 0.4) {
      this.totalPoopsPlanned = 2; // 30% chance for double poop
    } else {
      this.totalPoopsPlanned = 1; // 60% chance for single poop
    }

    const assetLoader = AssetLoader.getInstance();

    this.runFrames = [
      assetLoader.getTexture(`${rivalCatType}_run_1`),
      assetLoader.getTexture(`${rivalCatType}_run_2`),
      assetLoader.getTexture(`${rivalCatType}_run_3`),
    ];
    this.poopTexture = assetLoader.getTexture(`${rivalCatType}_poop`);

    this.sprite = new Sprite(this.runFrames[0]);
    this.sprite.anchor.set(0.5, 1);
    this.sprite.scale.set(0.5);

    // Mirror horizontally for walking left
    this.sprite.scale.x = -0.5;

    this.addChild(this.sprite);

    // Start off-screen to the right
    this.x = gameWidth + 100;
    this.y = groundY;

    // Target position for pooping (visible on screen)
    this.targetX = gameWidth - 150;
  }

  update(deltaTime: number): void {
    if (this.isDone) return;

    this.timer += deltaTime;

    switch (this.state) {
      case 'walk_in':
        this.updateWalkIn(deltaTime);
        break;
      case 'poop':
        this.updatePoop(deltaTime);
        break;
      case 'poop_pause':
        this.updatePoopPause(deltaTime);
        break;
      case 'walk_out':
        this.updateWalkOut(deltaTime);
        break;
    }
  }

  private updateWalkIn(deltaTime: number): void {
    // Move left
    this.x -= this.walkSpeed * deltaTime;

    // Animate running
    this.animationTimer += deltaTime;
    if (this.animationTimer >= this.animationSpeed) {
      this.animationTimer = 0;
      this.currentFrame = (this.currentFrame + 1) % this.runFrames.length;
      this.sprite.texture = this.runFrames[this.currentFrame];
    }

    // Check if reached target or time elapsed
    if (this.x <= this.targetX || this.timer >= this.walkInDuration) {
      this.x = this.targetX;
      this.state = 'poop';
      this.timer = 0;
      this.sprite.texture = this.poopTexture;
      this.sprite.scale.x = 0.5; // Don't mirror the poop sprite
      this.poopSpawnX = this.x;
      this.poopCount = 0;
    }
  }

  private updatePoop(_deltaTime: number): void {
    // Trigger poop spawn at the start of the poop state
    if (this.timer === _deltaTime) {
      // First frame of poop state
      if (this.onPoopSpawnCallback) {
        this.onPoopSpawnCallback();
      }
      this.poopCount++;
    }

    // Check if poop duration is complete
    if (this.timer >= this.poopDuration) {
      // If we haven't done all planned poops yet, go to pause state
      if (this.poopCount < this.totalPoopsPlanned) {
        this.state = 'poop_pause';
        this.timer = 0;
      } else {
        // Otherwise, start walking out
        this.state = 'walk_out';
        this.timer = 0;
        this.sprite.texture = this.runFrames[0];
        this.currentFrame = 0;
        this.animationTimer = 0;

        // Flip sprite to face right for walking out
        this.sprite.scale.x = 0.5;
      }
    }
  }

  private updatePoopPause(_deltaTime: number): void {
    // Stay in poop pose during pause
    if (this.timer >= this.poopPauseDuration) {
      // Go back to poop state for next poop
      this.state = 'poop';
      this.timer = 0;
    }
  }

  private updateWalkOut(deltaTime: number): void {
    // Move right
    this.x += this.walkSpeed * deltaTime;

    // Animate running
    this.animationTimer += deltaTime;
    if (this.animationTimer >= this.animationSpeed) {
      this.animationTimer = 0;
      this.currentFrame = (this.currentFrame + 1) % this.runFrames.length;
      this.sprite.texture = this.runFrames[this.currentFrame];
    }

    // Check if fully off-screen
    if (this.timer >= this.walkOutDuration) {
      this.isDone = true;
      if (this.onTelegraphCompleteCallback) {
        this.onTelegraphCompleteCallback();
      }
    }
  }

  onTelegraphComplete(callback: () => void): void {
    this.onTelegraphCompleteCallback = callback;
  }

  onPoopSpawn(callback: () => void): void {
    this.onPoopSpawnCallback = callback;
  }

  getIsDone(): boolean {
    return this.isDone;
  }

  getPoopSpawnX(): number {
    return this.poopSpawnX;
  }

  getTotalPoopsPlanned(): number {
    return this.totalPoopsPlanned;
  }
}
